import React from "react";
import { MessageCircleQuestion, Scale, UserCheck } from "lucide-react";

// Shared SBA / advisor front-door pieces (landing + personal front doors + attributed Ask).
const STEPS = [
  { icon: MessageCircleQuestion, title: "Ask your questions", text: "Ask Nauticus about Medicare in plain language, at your own pace." },
  { icon: Scale, title: "Explore plan options", text: "Compare plan facts for your county side by side." },
  { icon: UserCheck, title: "Request an advisor review", text: "When you're ready, a licensed insurance advisor can review your options with you." },
];

export const SbaSteps = () => (
  <section className="max-w-6xl mx-auto px-4 sm:px-8 py-10 sm:py-14" data-testid="sba-steps">
    <ol className="grid md:grid-cols-3 gap-4">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        return (
          <li key={s.title} className="bg-white rounded-2xl border border-[#E5E5E0] p-5 flex gap-4" data-testid={`sba-step-${i}`}>
            <div className="w-11 h-11 shrink-0 rounded-xl bg-[#0B192C] flex items-center justify-center"><Icon className="w-5 h-5 text-[#D4AF37]" /></div>
            <div>
              <p className="text-sm font-bold text-[#B89620]">Step {i + 1}</p>
              <h3 className="font-display font-bold text-lg text-[#0B192C]">{s.title}</h3>
              <p className="text-base text-[#4B5563] leading-relaxed">{s.text}</p>
            </div>
          </li>
        );
      })}
    </ol>
  </section>
);

export const AdvisorHelpSection = ({ onAdvisor, who = "our licensed insurance advisors" }) => (
  <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-10 sm:pb-14" data-testid="sba-advisor-help">
    <div className="rounded-2xl bg-[#0B192C] text-white p-6 sm:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center">
      <div>
        <h2 className="font-display font-bold text-xl sm:text-2xl">Licensed insurance advisor help, when you want it</h2>
        <p className="mt-3 text-base text-white/80 leading-relaxed max-w-2xl">
          You can explore on your own. If you'd like a person to help, {who} can answer questions,
          review the plans available to you and explain the details before you decide. There is no cost for this assistance.
        </p>
      </div>
      <button type="button" onClick={onAdvisor} className="h-12 px-6 rounded-full bg-[#D4AF37] text-[#0B192C] font-bold hover:bg-[#B89620]" data-testid="sba-advisor-help-cta">
        Request advisor help
      </button>
    </div>
  </section>
);

// Real capture of the live Ask Nauticus START screen (no plan data). Illustration only, not interactive.
export const ComparePreview = () => (
  <figure className="rounded-2xl border border-[#E5E5E0] bg-white shadow-xl overflow-hidden select-none" data-testid="compare-preview">
    <figcaption className="flex items-center justify-between px-4 py-2 bg-[#0B192C] text-white text-sm font-semibold">
      <span>Explore Ask Nauticus</span><span className="text-white/70 font-normal">Preview</span>
    </figcaption>
    <img src="/img/ask-start-example.webp" alt="Preview of the Ask Nauticus start screen with example questions"
      className="w-full h-auto pointer-events-none" loading="eager" draggable="false" />
    <p className="px-4 py-2 text-sm text-[#4B5563] border-t border-[#E5E5E0]">Preview of the start screen. Use the Ask Nauticus button to begin.</p>
  </figure>
);

// TPMO disclaimer (42 CFR 422.2267(e)(41)). The count sentence renders ONLY from approved, configured counts;
// never placeholders or CMS plan totals. Readable size (16px) and contrast.
export const TpmoDisclosure = ({ entity = "Senior Benefit Advisors of Florida, LLC", tpmo, dark = false, testid = "tpmo-disclosure" }) => {
  const approved = tpmo && tpmo.approved && Number(tpmo.org_count) > 0 && Number(tpmo.plan_count) > 0;
  const tone = dark ? "text-white/90 border-white/20" : "text-[#1F2937] border-[#D7D4CB] bg-white";
  return (
    <div className={`rounded-xl border p-4 text-base leading-relaxed ${tone}`} data-testid={testid}>
      <p data-testid={`${testid}-tpmo`}>
        {approved
          ? `We do not offer every plan available in your area. Currently we represent ${tpmo.org_count} organizations which offer ${tpmo.plan_count} products in your area. `
          : "We do not offer every plan available in your area. "}
        Please contact <a href="https://www.medicare.gov" target="_blank" rel="noreferrer" className="underline font-semibold">Medicare.gov</a> or
        {" "}<a href="tel:+18006334227" className="underline font-semibold">1-800-MEDICARE</a> (24 hours a day, 7 days a week) to get information on all of your options.
      </p>
      <p className="mt-2" data-testid={`${testid}-nonaffiliation`}>Not affiliated with or endorsed by the U.S. Government or the Federal Medicare Program.</p>
      <p className="mt-2">{entity} is a licensed insurance agency. Nauticus is a technology platform by Navillus Technologies LLC.</p>
    </div>
  );
};
